import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Image } from '@codes/ui-kit/components/ui/image';
import { ChatView } from '@lemoncloud/ssocio-chatbots-api';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { AiLogoDarkImage, AiLogoImage } from '@codes/assets';

import { MessageBubble } from './MessageBubble';
import { MessageTimestamp } from './MessageTimestamp';
import { extractDocumentId } from '../utils/document';

import type { DocumentData } from '../types/document';
import type { MetaInfo } from '../types/meta';

interface ChatViewWithError extends ChatView {
    isError?: boolean;
    usage?: {
        promptToken?: number;
        completionToken?: number;
        totalToken?: number;
    };
    attachments?: Array<{
        url: string;
        name?: string;
        contentType: string;
    }>;
    meta$$?: MetaInfo[];
}

export interface ChatMessageProps {
    message: ChatViewWithError;
    onDocumentSelect?: (documentId: string) => Promise<DocumentData | null>;
}

// Animation variants for different animation types
const messageAnimationVariants = {
    initial: {
        opacity: 0,
        y: 10,
    },
    animate: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.3,
        },
    },
    slideIn: {
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 500,
            damping: 30,
        },
    },
    bounce: {
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 700,
            damping: 25,
        },
    },
};

// Helper function to convert data URL to a Uint8Array
function dataUrlToUint8Array(data: string) {
    const base64 = data.split(',')[1];
    const buf = Buffer.from(base64, 'base64');
    return new Uint8Array(buf);
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message, onDocumentSelect }) => {
    const { t } = useTranslation();
    const [showUsage, setShowUsage] = useState(false);
    const [showDocuments, setShowDocuments] = useState(false);

    const isQuery = message.stereo === 'query';
    const isError = message.isError;
    const hasUsage =
        message.usage$ && (message.usage$.promptToken || message.usage$.completionToken || message.usage$.totalToken);
    const hasDocuments = message.documentIds && message.documentIds.length > 0;

    // Handle document selection
    const handleDocumentSelect = useCallback(
        async (documentId: string) => {
            if (!onDocumentSelect) {
                return;
            }
            await onDocumentSelect(documentId);
        },
        [onDocumentSelect]
    );

    const handlePhoneClick = useCallback((phone: string) => {
        if (!phone) {
            return;
        }

        window.location.href = `tel:${phone}`;
    }, []);

    const handleLinkClick = useCallback((url: string) => {
        if (!url) {
            return;
        }

        window.open(url, '_blank', 'noopener,noreferrer');
    }, []);

    const handleImageClick = useCallback((imageUrl: string) => {
        if (!imageUrl) {
            return;
        }

        window.open(imageUrl, '_blank', 'noopener,noreferrer');
    }, []);

    const handleToggleUsage = useCallback(() => {
        setShowUsage(!showUsage);
        if (!showUsage) {
            setShowDocuments(false);
        }
    }, [showUsage]);

    const handleToggleDocuments = useCallback(() => {
        setShowDocuments(!showDocuments);
        if (!showDocuments) {
            setShowUsage(false);
        }
    }, [showDocuments]);

    // Generate a unique ID for this message
    const messageId = useMemo(() => `msg-${Math.random().toString(36).substring(2, 11)}`, []);

    // Convert attachments to File objects
    const files = useMemo(() => {
        if (!message.attachments) {
            return undefined;
        }
        return message.attachments.map(attachment => {
            const dataArray = dataUrlToUint8Array(attachment.url);
            const file = new File([dataArray], attachment.name ?? 'Unknown', {
                type: attachment.contentType,
            });
            return file;
        });
    }, [message.attachments]);

    // Shared content for copy button
    const copyButtonContent = useMemo(() => {
        return message.content ?? '';
    }, [message.content]);

    const messageClassName = isQuery
        ? isError
            ? 'max-w-[80%] bg-accent rounded-[18px] rounded-tr-[3px] py-2 px-[14px] border border-destructive transition-all'
            : 'max-w-[80%] bg-accent rounded-[18px] rounded-tr-[3px] py-2 px-[14px]'
        : isError
        ? 'text-destructive-foreground rounded-[18px] py-[10px] border border-destructive transition-all'
        : 'text-title py-[10px] rounded-[18px]';

    return (
        <motion.div
            className={`flex ${isQuery ? 'justify-end' : 'justify-start'}`}
            role="group"
            aria-label={`${isQuery ? 'User' : 'Assistant'} message`}
            initial={messageAnimationVariants.initial}
            animate={messageAnimationVariants.animate}
        >
            <div className={`relative group max-w-[85%] max-xs:max-w-full ${messageClassName}`}>
                <div className="flex gap-3">
                    {!isQuery && (
                        <div className="flex-shrink-0">
                            <Image src={AiLogoImage} darkSrc={AiLogoDarkImage} className="w-5 h-5" alt="AI Assistant" />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <MessageBubble
                            isUser={isQuery}
                            messageId={messageId}
                            content={message.content}
                            copyButtonContent={copyButtonContent}
                            files={files}
                            meta$$={message.meta$$}
                            onPhoneClick={handlePhoneClick}
                            onLinkClick={handleLinkClick}
                            onImageClick={handleImageClick}
                        />

                        <div className={`flex items-center ${isQuery ? 'justify-end' : 'justify-start'}`}>
                            {message.createdAt && <MessageTimestamp createdAt={message.createdAt} />}
                            {hasUsage && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                                    onClick={handleToggleUsage}
                                >
                                    {showUsage ? (
                                        <ChevronUp className="w-3 h-3 mr-1" />
                                    ) : (
                                        <ChevronDown className="w-3 h-3 mr-1" />
                                    )}
                                    {t('agent.form.responseTest.tokens', '토큰')}
                                </Button>
                            )}
                            {hasDocuments && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                                    onClick={handleToggleDocuments}
                                >
                                    {showDocuments ? (
                                        <ChevronUp className="w-3 h-3 mr-1" />
                                    ) : (
                                        <ChevronDown className="w-3 h-3 mr-1" />
                                    )}
                                    {t('ai.chatbot.reference_documents', '참조 문서')}
                                </Button>
                            )}
                        </div>

                        {/* Usage 데이터 */}
                        {hasUsage && showUsage && (
                            <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3 space-y-2 mt-3">
                                <div className="flex items-center justify-between">
                                    <span>{t('agent.form.responseTest.promptTokens', '프롬프트 토큰')}:</span>
                                    <span className="font-mono font-medium">{message.usage$?.promptToken || 0}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span>{t('agent.form.responseTest.completionTokens', '완성 토큰')}:</span>
                                    <span className="font-mono font-medium">
                                        {message.usage$?.completionToken || 0}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between font-medium border-t border-border/50 pt-2">
                                    <span>{t('agent.form.responseTest.totalTokens', '총 토큰')}:</span>
                                    <span className="font-mono">{message.usage$?.totalToken || 0}</span>
                                </div>
                            </div>
                        )}

                        {/* Documents 데이터 */}
                        {hasDocuments && showDocuments && (
                            <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3 space-y-2 mt-3">
                                {message.documentIds?.map((documentId: string, idx: number) => (
                                    <div
                                        key={idx}
                                        className="cursor-pointer hover:text-foreground transition-colors"
                                        onClick={() => handleDocumentSelect(extractDocumentId(documentId))}
                                    >
                                        <span className="font-mono font-medium">{documentId}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};
